import { isDeviceFlowPath, parseFlowFile, serializeDeviceFlowFile } from '../flow/flowFile';
import { deviceApi } from '../api';
import {
  SETTINGS_PATH,
  ensureCatalogEntry,
  parseSettingsContent,
  serializeSettingsContent,
} from './catalogSync';
import { validateDeployTarget } from './deployValidation';

export type DeployCatalogOptions = {
  syncCatalog?: boolean;
  label?: string;
  hardwareRef?: string;
  setActive?: boolean;
};

export type DeployResult = {
  taskId: string;
  catalogSynced?: boolean;
  catalogEntryAdded?: boolean;
  warnings?: string[];
};

function operationalContent(path: string, content: string): string {
  if (!isDeviceFlowPath(path)) {
    return content;
  }
  try {
    const name = path.match(/\/([^/]+)\.json$/i)?.[1] || 'flow';
    return serializeDeviceFlowFile(parseFlowFile(content, name));
  } catch {
    return content;
  }
}

export async function deployJsonToDevice(
  deviceId: string,
  path: string,
  content: string,
  options?: DeployCatalogOptions,
): Promise<DeployResult> {
  const warnings = await validateDeployTarget(deviceId, path, {
    hardwareRef: options?.hardwareRef,
  });

  let catalogSynced = false;
  let catalogEntryAdded = false;

  if (options?.syncCatalog !== false && path !== SETTINGS_PATH) {
    const stored = await deviceApi.getStoredFile(deviceId, SETTINGS_PATH);
    if (stored?.content) {
      const parsed = parseSettingsContent(stored.content);
      const { settings: nextSettings, result } = ensureCatalogEntry(parsed, path, {
        label: options?.label,
        hardwareRef: options?.hardwareRef,
        setActive: options?.setActive,
      });
      if (result.changed) {
        await deviceApi.saveStoredFile(deviceId, SETTINGS_PATH, serializeSettingsContent(nextSettings));
        catalogSynced = true;
        catalogEntryAdded = !!result.missingOnDevice;
      }
    }
  }

  const body = operationalContent(path, content);
  await deviceApi.saveStoredFile(deviceId, path, body);
  const task = await deviceApi.pushStoredFile(deviceId, path);
  return {
    taskId: task.id,
    catalogSynced,
    catalogEntryAdded,
    warnings,
  };
}
